import { useState } from "react";
import { toast } from "sonner";
import { ExternalLink, MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { CrmUserDetail } from "@/services/adminCrm";
import { launchAsUser } from "@/services/adminCrm";

interface Props {
  data: CrmUserDetail;
}

const stateColors: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  comp: "bg-blue-100 text-blue-800 border-blue-200",
  b2b: "bg-green-100 text-green-800 border-green-200",
  canceling: "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  canceled: "bg-red-100 text-red-800 border-red-200",
  past_due: "bg-red-100 text-red-800 border-red-200",
  none: "bg-gray-100 text-gray-600 border-gray-200",
};

const avatarColors = [
  "bg-blue-500",
  "bg-green-500",
  "bg-purple-500",
  "bg-amber-500",
  "bg-pink-500",
  "bg-teal-500",
  "bg-indigo-500",
  "bg-rose-500",
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export function UserDetailHeader({ data }: Props) {
  const { user, profile } = data;
  const [launching, setLaunching] = useState(false);
  const displayName = profile.displayName || user.email;
  const initials =
    [profile.firstName?.[0], profile.lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
    user.email[0].toUpperCase();

  const state = user.subscriptionState || "none";
  const hasAccess = user.hasAccess ?? ["b2b", "active", "comp", "canceling"].includes(state);

  const activationColors: Record<string, string> = {
    activated: "border-green-300 text-green-700",
    invited: "border-blue-300 text-blue-700",
    lapsed: "border-amber-300 text-amber-700",
  };
  const activationLabels: Record<string, string> = {
    activated: "Activated",
    invited: "Invited",
    lapsed: "Lapsed",
  };

  function handleAction(action: string) {
    toast.info(`${action} - Coming soon`);
  }

  async function handleLaunch(path = "/") {
    if (!user.customer) return toast.error("User has no customer");
    try {
      setLaunching(true);
      const url = await launchAsUser(user.customer.customerId, user.email, path);
      window.open(url, "_blank");
    } catch (err: any) {
      toast.error(`Launch failed: ${err.message}`);
    } finally {
      setLaunching(false);
    }
  }

  return (
    <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div
          className={`h-12 w-12 rounded-full ${getAvatarColor(displayName)} flex items-center justify-center text-white font-semibold text-lg shrink-0`}
        >
          {initials}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold truncate">{displayName}</h1>
            <Badge variant="outline" className="text-xs capitalize">
              {user.userType}
            </Badge>
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${hasAccess ? "bg-green-500" : "bg-red-500"}`}
              title={hasAccess ? "Has access" : "No access"}
            />
            <Badge
              variant="outline"
              className={`text-xs ${stateColors[state] || stateColors.none}`}
            >
              {state === "b2b"
                ? "B2B"
                : state.charAt(0).toUpperCase() + state.slice(1).replace("_", " ")}
            </Badge>
            {user.activationStage && (
              <Badge
                variant="outline"
                className={`text-xs ${activationColors[user.activationStage] || activationColors.lapsed}`}
              >
                {activationLabels[user.activationStage] || "Lapsed"}
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <span>{user.email}</span>
            {user.customer.name && (
              <>
                <span className="text-muted-foreground/50">|</span>
                <span>{user.customer.name}</span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            disabled={launching}
            onClick={() => handleLaunch("/")}
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            {launching ? "Launching..." : "Launch As"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleAction("Reset Onboarding")}>
                Reset Onboarding
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction("Resend Invite")}>
                Resend Invite
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction("Send Login Link")}>
                Send Login Link
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onClick={() => handleAction("Delete User")}
              >
                Delete User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
